/**
 * XRPlaneSystem — tracks real-world planes from the plane-detection feature
 * (Quest 3/3S scene planes with semantic labels, Android Chrome ARCore).
 *
 * Each frame, `frame.detectedPlanes` is diffed against the previous set:
 * new planes get a stable numeric id + an `xr:plane-added` event, changed
 * planes (pose or `lastChangedTime`) update in place with `xr:plane-updated`,
 * vanished planes emit `xr:plane-removed`. The live map lives on
 * XRSessionState.detectedPlanes for renderers/games to consume.
 *
 * Phase: RenderSub, SubPhase: PreUpdate, Priority: 11
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EventBus } from '../../core/EventBus.js';
import type { XRSessionState, XRPlaneInfo } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';

export class XRPlaneSystem implements ISystem {
  readonly name = 'XRPlane';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = 11;

  private _state: XRSessionState;
  private _events: EventBus;
  private _planeSession: XRSession | null = null;
  private _warnedMissingFrame = false;
  /** Scratch set reused per frame to detect removals without allocating. */
  private readonly _current = new Set<XRPlane>();

  constructor(state: XRSessionState, events: EventBus) {
    this._state = state;
    this._events = events;
  }

  execute(_dt: number, _world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('plane-detection')) return;

    const session = this._state.session!;
    if (session !== this._planeSession) {
      this._planeSession = session;
      this._state.planeIdMap.clear();
      this._state.detectedPlanes.clear();
    }

    const frame = (globalThis as { __xrFrame?: XRFrame }).__xrFrame;
    if (!frame) {
      if (!this._warnedMissingFrame) {
        Logger.warn('XRPlane', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    const detected = frame.detectedPlanes;
    const refSpace = this._state.referenceSpace;
    if (!detected || !refSpace) return;

    this._current.clear();
    for (const plane of detected) {
      this._current.add(plane);

      let id = this._state.planeIdMap.get(plane);
      const isNew = id === undefined;
      if (id === undefined) {
        id = this._state.nextPlaneId();
        this._state.planeIdMap.set(plane, id);
      }

      const existing = this._state.detectedPlanes.get(id);
      // Skip untouched planes: pose still refreshes cheaply below only when
      // the plane reports a change or is new (planes are near-static).
      if (existing && existing.lastChangedTime === plane.lastChangedTime) {
        // Pose can still drift with tracking refinement — refresh matrix only.
        const pose = frame.getPose(plane.planeSpace, refSpace);
        if (pose) existing.matrix.set(pose.transform.matrix);
        continue;
      }

      const pose = frame.getPose(plane.planeSpace, refSpace);
      if (!pose) continue;

      const info: XRPlaneInfo = existing ?? {
        id,
        matrix: new Float32Array(16),
        polygon: [],
        orientation: plane.orientation,
        semanticLabel: plane.semanticLabel ?? '',
        lastChangedTime: plane.lastChangedTime,
      };
      info.matrix.set(pose.transform.matrix);
      info.polygon = plane.polygon.map((p) => [p.x, p.z] as [number, number]);
      info.orientation = plane.orientation;
      info.semanticLabel = plane.semanticLabel ?? '';
      info.lastChangedTime = plane.lastChangedTime;
      this._state.detectedPlanes.set(id, info);

      this._events.emit(isNew ? 'xr:plane-added' : 'xr:plane-updated', {
        planeId: id,
        orientation: info.orientation,
        semanticLabel: info.semanticLabel,
      });
    }

    // Removals: anything mapped that the frame no longer reports.
    for (const [plane, id] of this._state.planeIdMap) {
      if (this._current.has(plane)) continue;
      this._state.planeIdMap.delete(plane);
      this._state.detectedPlanes.delete(id);
      this._events.emit('xr:plane-removed', { planeId: id });
    }
  }
}
